/**
 * Cohort Comparison Chart Component
 * Displays bar chart comparing user access counts vs peer cohort averages
 * Uses Recharts for visualization
 */

import React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { Users } from 'lucide-react';

const CohortComparisonChart = ({ cohortData }) => {
  if (!cohortData || cohortData.length === 0) {
    return (
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <Users size={20} style={{ marginRight: 8 }} />
            Peer Cohort Comparison
          </Typography>
          <Typography color="text.secondary">No data available</Typography>
        </CardContent>
      </Card>
    );
  }

  // Transform data for bar chart
  const chartData = cohortData.map((cohort) => ({
    cohortName: cohort.cohort_name || 'Unknown',
    avgGroups: parseFloat(cohort.avg_group_count) || 0,
    avgApps: parseFloat(cohort.avg_app_count) || 0,
    avgPrivileged: parseFloat(cohort.avg_privileged_count) || 0,
    memberCount: parseInt(cohort.member_count) || 0
  }));

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const cohort = chartData.find(c => c.cohortName === label);
      return (
        <div
          style={{
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: '4px',
            padding: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}
        >
          <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>{label}</p>
          <p style={{ margin: '4px 0', fontSize: '0.9rem' }}>
            Avg Groups: <strong>{cohort.avgGroups.toFixed(1)}</strong>
          </p>
          <p style={{ margin: '4px 0', fontSize: '0.9rem' }}>
            Avg Apps: <strong>{cohort.avgApps.toFixed(1)}</strong>
          </p>
          <p style={{ margin: '4px 0', fontSize: '0.9rem' }}>
            Avg Privileged: <strong>{cohort.avgPrivileged.toFixed(1)}</strong>
          </p>
          <p style={{ margin: '4px 0', fontSize: '0.9rem', color: '#666' }}>
            Members: {cohort.memberCount}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card sx={{ mb: 4 }}>
      <CardContent>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Users size={20} style={{ marginRight: 8 }} />
          Peer Cohort Comparison
        </Typography>
        <Typography color="text.secondary" sx={{ fontSize: '0.9rem', mb: 2 }}>
          Average access counts by peer cohort (job title, department, etc.)
        </Typography>

        <ResponsiveContainer width="100%" height={400}>
          <BarChart
            data={chartData}
            margin={{ top: 20, right: 30, bottom: 100, left: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="cohortName"
              angle={-45}
              textAnchor="end"
              height={100}
              interval={0}
            />
            <YAxis
              label={{ value: 'Average Count', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar dataKey="avgGroups" name="Avg Groups" fill="#3b82f6" />
            <Bar dataKey="avgApps" name="Avg Apps" fill="#8b5cf6" />
            <Bar dataKey="avgPrivileged" name="Avg Privileged" fill="#f59e0b" />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default CohortComparisonChart;
